import { Affix, TextField } from '@warp-ds/react';
import type { FocusEventHandler, KeyboardEventHandler, Ref } from 'react';

import { replaceDashWithComma } from '../../../../util/formatting';
import type { AutocompleteOption, AutocompleteProps } from '../../../types';

import styles from '../Autocomplete.module.css';

import { AutocompleteIcon } from './AutocompleteIcon';

export type DropdownInputFieldProps = {
    localId: string;
    label: string;
    value: string;
    placeholder?: string | null;
    error?: string | null;
    vertical: AutocompleteProps['vertical'];
    departure?: AutocompleteProps['departure'];
    showDropdown: boolean;
    selectedOption: AutocompleteOption | null;
    hasSelectedOption: boolean;
    selectedIndex: number;
    onKeyDown: KeyboardEventHandler<HTMLInputElement>;
    onBlur: FocusEventHandler<HTMLInputElement>;
    onChange: (value: string) => void;
    onClick: () => void;
    inputRef?: Ref<HTMLInputElement>;
    containerRef?: Ref<HTMLDivElement>;
    autoFocus?: boolean;
    clearAll: () => void;
};

export const AutocompleteInputField = ({
    localId,
    label,
    value,
    placeholder,
    error,
    vertical,
    departure,
    showDropdown,
    selectedOption,
    selectedIndex,
    onKeyDown,
    onBlur,
    onChange,
    onClick,
    inputRef,
    containerRef,
    autoFocus,
    clearAll,
}: DropdownInputFieldProps) => (
    <div className={styles['dropdown-input-field']} ref={containerRef}>
        <label htmlFor={localId} className={styles['dropdown-input-field__label']}>
            {label}
        </label>
        <div className={styles['dropdown-input-field__textfield-wrapper']}>
            <div className={styles['dropdown-input-field__prefix']}>
                <AutocompleteIcon option={selectedOption} vertical={vertical} departure={departure} />
            </div>
            <TextField
                ref={inputRef}
                type="text"
                aria-haspopup="listbox"
                id={localId}
                data-testid={localId}
                value={replaceDashWithComma(value)}
                placeholder={placeholder ?? undefined}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
                onChange={(e) => onChange(e.target.value)}
                onClick={onClick}
                inputMode="text"
                role="combobox"
                aria-autocomplete="list"
                aria-activedescendant={selectedIndex !== -1 ? `${localId}-option-${selectedIndex}` : undefined}
                aria-controls={`${localId}-options`}
                aria-expanded={showDropdown}
                invalid={!!error}
                helpText={error ?? undefined}
                className={styles['dropdown-input-field__textfield']}
                autoFocus={autoFocus}>
                {value?.length > 0 && <Affix suffix clear onClick={clearAll} aria-label="Nullstill" />}
            </TextField>
        </div>
    </div>
);
